import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Avatar } from "@mui/material";
import { Helmet } from 'react-helmet';
import './BrainDrain.css';

const BrainDrainQuestion = () => {
  const { id } = useParams();
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cdn = "https://files.geetsuhane.com/";

  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_END_POINT}/braindrain/question/${id}`);
        if (!response.ok) {
          throw new Error('Question not found');
        }
        const data = await response.json();
        setQuestion(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [id]);

  if (loading) {
    return (
      <div className="main-content-wrap">
        <div className="brain-drain-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="main-content-wrap">
        <Helmet>
          <title>Question Not Found - Brain Drain</title>
        </Helmet>
        <div className="brain-drain-container">
          <div className="error-message">
            <h2>Question not found</h2>
            <p>This question may have been removed or doesn't exist.</p>
            <Link to="/braindrain" className="play-btn">
              Play Brain Drain
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="main-content-wrap">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Brain Drain #{id}: {question.question_en?.substring(0, 50)}... - Answer | Geet Suhane</title>
        <meta name="description" content={`Answer: ${question.answer || 'Not yet answered'}. ${question.winner_name ? `Winner: ${question.winner_name}.` : ''} Play more Brain Drain riddles on Geet Suhane!`} />
        <meta name="keywords" content={`riddle ${id}, brain teaser, ${question.answer}, Hindi riddle, puzzle answer`} />
        <meta property="og:title" content={`Brain Drain Riddle #${id} | Geet Suhane`} />
        <meta property="og:description" content={`${question.question_en} Answer revealed!`} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`Brain Drain Riddle #${id}`} />
        <meta name="twitter:description" content={`${question.question_en?.substring(0, 100)} Answer revealed!`} />
        <link rel="canonical" href={`https://geetsuhane.com/braindrain/q/${id}`} />
      </Helmet>

      <div className="brain-drain-container archive-page">
        {/* Header */}
        <div className="brain-drain-header">
          <span className="brain-icon">&#129504;</span>
          <span className="brain-title">Brain Drain #{id}</span>
          <span className="brain-icon">&#129504;</span>
        </div>

        {/* Question */}
        <div className="archive-question-area">
          {question.category && (
            <div className="question-category">{question.category}</div>
          )}
          <div className="question-text question-en">
            {question.question_en}
          </div>
          <div className="question-text question-hi">
            {question.question_hi}
          </div>
        </div>

        {/* Answer Section (only if completed) */}
        {question.status === 'completed' && question.answer && (
          <div className="archive-answer-section">
            <div className="answer-label">Answer</div>
            <div className="answer-value">{question.answer}</div>
          </div>
        )}

        {/* Winner Section (only if there's a winner) */}
        {question.winner_name && (
          <div className="archive-winner-section">
            <div className="winner-label">&#127942; Winner</div>
            <div className="winner-info">
              <Avatar
                src={question.winner_picture?.startsWith('http') ? question.winner_picture : cdn + question.winner_picture}
                alt={question.winner_name}
                sx={{ width: 50, height: 50 }}
              />
              <span className="winner-name">{question.winner_name}</span>
            </div>
            {question.used_at && (
              <div className="answered-date">
                Answered on {formatDate(question.used_at)}
              </div>
            )}
          </div>
        )}

        {/* Active question message */}
        {question.status === 'active' && (
          <div className="active-question-message">
            <p>This question is currently active!</p>
            <Link to="/braindrain" className="play-btn pulse">
              Play Now
            </Link>
          </div>
        )}

        {/* CTA */}
        <div className="archive-cta">
          <Link to="/braindrain" className="play-btn">
            Play Current Question
          </Link>
          <Link to="/braindrain/archive" className="archive-btn">
            View All Questions
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BrainDrainQuestion;
